import { NewFeatures } from "@/components/tooltip/new-features.ts";
import { NewFeatureTooltip } from "@/components/tooltip/NewFeatureTooltip.tsx";
import { viewFilterCount } from "@/pages/employees/index/use-employee-views-state.ts";
import { ApiEmployeeListView } from "@/types/employees/employee-view.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface EmployeeViewSelectorProps {
  views: ApiEmployeeListView[];
  activeView?: ApiEmployeeListView;
  isDefaultActive: boolean;
  isDirty: boolean;
  isUpdating: boolean;
  onSelectView: (viewId?: number) => void;
  onUpdateView: () => void;
  onSaveAsNewView: () => void;
  onManageViews: () => void;
}

export function EmployeeViewSelector({
  views,
  activeView,
  isDefaultActive,
  isDirty,
  isUpdating,
  onSelectView,
  onUpdateView,
  onSaveAsNewView,
  onManageViews,
}: EmployeeViewSelectorProps) {
  const { t } = useTranslation();

  const activeTitle = isDefaultActive || !activeView ? t("core:all_employees") : activeView.title;
  const hasUnsavedChanges = isDirty;

  return (
    <DropdownMenu
      trigger={
        <NewFeatureTooltip
          feature={NewFeatures.employee_saved_views}
          title={t("core:saved_views_feature_title")}
          description={t("core:saved_views_feature_description")}
        >
          <Button
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          >
            {activeTitle}
            {hasUnsavedChanges && " *"}
          </Button>
        </NewFeatureTooltip>
      }
    >
      <DropdownMenuItem
        title={t("core:all_employees")}
        description={t("core:view_filters_count", { count: 0 })}
        isSelected={isDefaultActive}
        onClick={() => onSelectView(undefined)}
      />
      {views.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("core:saved_views_label")}</DropdownMenuLabel>
          {views.map((view) => (
            <DropdownMenuItem
              key={view.id}
              title={view.title}
              description={t("core:view_filters_count", { count: viewFilterCount(view) })}
              isSelected={activeView?.id === view.id}
              onClick={() => onSelectView(view.id)}
            />
          ))}
        </>
      )}
      <DropdownMenuSeparator />

      <DropdownMenuItem
        title={t("core:save_as_new_view")}
        description={t("core:save_current_filters_and_columns")}
        onClick={onSaveAsNewView}
      />

      {!isDefaultActive && isDirty && (
        <DropdownMenuItem
          title={t("core:update_view", { title: activeTitle })}
          description={t("core:save_changes_to_view")}
          disabled={isUpdating}
          onClick={onUpdateView}
        />
      )}

      {views.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem title={t("core:manage_views")} onClick={onManageViews} />
        </>
      )}
    </DropdownMenu>
  );
}
